/**
 * Checks whether the current user has a specific resource:action permission
 * in the selected organization.
 */

import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'

import { type RootState } from '~redux/rootReducer'
import { type AccessAction, type GeneralResource, type IdentityId, type UserId } from '~types'
import { loadResourcePermittedOrgIdentities } from '~utils/permissionsUtils'

type UsePermissionResult = {
  allowedIdentities: IdentityId[]
  loading: boolean
}

export const usePermission = (
  resource: GeneralResource | null,
  action: AccessAction | null,
): UsePermissionResult => {
  const userId = useSelector((state: RootState) => state?.auth.userId)
  const [allowedIdentities, setAllowedIdentities] = useState<IdentityId[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (resource == null || action == null || !userId) {
      setAllowedIdentities([])
      setLoading(false)
      return
    }

    let isCurrent = true
    setLoading(true)
    setAllowedIdentities([])
    const checkPermission = async (): Promise<void> => {
      if (!userId) {
        setLoading(false)
        return
      }
      try {
        const resourcePolicies = await loadResourcePermittedOrgIdentities(
          userId as UserId,
          resource,
        )
        const permittedOrgIdentities = resourcePolicies[action] ?? []
        if (isCurrent) {
          setAllowedIdentities(permittedOrgIdentities)
        }
      } catch (error) {
        console.error('Error loading resource permissions:', error)
      } finally {
        if (isCurrent) {
          setLoading(false)
        }
      }
    }
    checkPermission()
    return (): void => {
      isCurrent = false
    }
  }, [userId, resource, action])

  return { allowedIdentities: allowedIdentities, loading }
}
